import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { setImpersonationHeaders } from '../lib/api';

/**
 * ImpersonationContext — מאפשר לסופר-אדמין "להיכנס" לעמותה וללא להשאיר עקבות:
 * - אין יצירת משתמש בעמותה.
 * - אין שינוי JWT. הטוקן נשאר של הסופר-אדמין.
 * - כל בקשה נשלחת עם header X-Impersonate-Org, ו-backend מטפל ב-scoping.
 * - ה-state שמור ב-memory בלבד — רענון דף מאפס את המצב (תכונה, לא באג).
 */

interface ImpersonationState {
  orgId: string | null;
  orgName: string | null;
  allowWrite: boolean;
}

interface ImpersonationContextValue extends ImpersonationState {
  isImpersonating: boolean;
  start: (orgId: string, orgName: string) => void;
  stop: () => void;
  setAllowWrite: (allow: boolean) => void;
}

const ImpersonationContext = createContext<ImpersonationContextValue | null>(null);

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ImpersonationState>({
    orgId: null,
    orgName: null,
    allowWrite: false,
  });

  // מעדכן את axios עם ה-headers הנוכחיים
  const syncHeaders = useCallback((next: ImpersonationState) => {
    setImpersonationHeaders({
      orgId: next.orgId,
      allowWrite: next.allowWrite,
    });
  }, []);

  const start = useCallback(
    (orgId: string, orgName: string) => {
      const next: ImpersonationState = { orgId, orgName, allowWrite: false };
      setState(next);
      syncHeaders(next);
    },
    [syncHeaders],
  );

  const stop = useCallback(() => {
    const next: ImpersonationState = { orgId: null, orgName: null, allowWrite: false };
    setState(next);
    syncHeaders(next);
  }, [syncHeaders]);

  const setAllowWrite = useCallback(
    (allow: boolean) => {
      setState((prev) => {
        const next = { ...prev, allowWrite: allow };
        syncHeaders(next);
        return next;
      });
    },
    [syncHeaders],
  );

  const value = useMemo<ImpersonationContextValue>(
    () => ({
      ...state,
      isImpersonating: !!state.orgId,
      start,
      stop,
      setAllowWrite,
    }),
    [state, start, stop, setAllowWrite],
  );

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
};

export function useImpersonation(): ImpersonationContextValue {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) {
    // fallback בטוח — אם מישהו קרא לזה מחוץ ל-Provider, מחזיר no-op
    return {
      orgId: null,
      orgName: null,
      allowWrite: false,
      isImpersonating: false,
      start: () => {},
      stop: () => {},
      setAllowWrite: () => {},
    };
  }
  return ctx;
}
